import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useConsumerAuth } from '../../context/ConsumerAuthContext';
import axios from 'axios';
import { 
  Car, Home, FileText, CreditCard, CheckCircle, Clock, AlertCircle, 
  LogOut, User, Bell, ChevronRight, Shield, TrendingUp, DollarSign,
  ArrowRight, Plus, Percent, Calendar, Building2, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConsumerDashboard = () => {
  const navigate = useNavigate();
  const { consumer, token, logout, loading: authLoading } = useConsumerAuth();
  const [applications, setApplications] = useState([]);
  const [allOffers, setAllOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!authLoading && !token) {
      navigate('/beth-finance/login');
    }
  }, [token, authLoading, navigate]);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const response = await axios.get(`${API}/consumer/applications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setApplications(response.data.applications || []);
      } catch (err) {
        console.error('Failed to fetch applications:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchAllOffers = async () => {
      try {
        // Fetch applications first to get all application IDs
        const appsRes = await axios.get(`${API}/consumer/applications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const apps = appsRes.data.applications || [];
        
        // Fetch offers for each application
        const offersPromises = apps.map(app => 
          axios.get(`${API}/lenders/offers/${app.application_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: { offers: [] } }))
        );
        
        const offersResults = await Promise.all(offersPromises);
        const allOffersData = offersResults.flatMap((res, idx) => 
          (res.data.offers || []).map(offer => ({
            ...offer,
            loan_type: apps[idx].loan_type
          }))
        );
        
        setAllOffers(allOffersData);
      } catch (err) {
        console.error('Failed to fetch offers:', err);
      }
    };

    if (token) {
      fetchApplications();
      fetchAllOffers();
    }
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/beth-finance');
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': 
      case 'offer_accepted':
        return 'bg-green-100 text-green-800';
      case 'pending': 
      case 'pending_payment':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing': 
      case 'submitted_to_lenders':
      case 'payment_complete':
        return 'bg-blue-100 text-blue-800';
      case 'rejected': 
      case 'declined':
        return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': 
      case 'offer_accepted':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending': 
      case 'pending_payment':
        return <Clock className="w-4 h-4" />;
      case 'processing': 
      case 'submitted_to_lenders':
      case 'payment_complete':
        return <TrendingUp className="w-4 h-4" />;
      case 'rejected': 
      case 'declined':
        return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatStatus = (status) => {
    const statusMap = {
      'offer_accepted': 'Offer Accepted',
      'submitted_to_lenders': 'Submitted',
      'pending_payment': 'Pending Payment',
      'payment_complete': 'Paid'
    };
    return statusMap[status?.toLowerCase()] || status?.replace(/_/g, ' ');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#012169]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#012169] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/beth-finance" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#E31837] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">B</span>
              </div>
              <span className="text-xl font-bold tracking-tight">BETH Finance</span>
            </Link>

            <div className="flex items-center space-x-6">
              <button className="relative">
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#E31837] rounded-full text-xs flex items-center justify-center">2</span>
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium">{consumer?.first_name || 'User'}</span>
              </div>
              <button 
                onClick={handleLogout}
                className="flex items-center text-sm hover:text-[#E31837] transition-colors"
              >
                <LogOut className="w-4 h-4 mr-1" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'applications', label: 'My Applications' },
              { id: 'offers', label: 'Loan Offers' },
              { id: 'profile', label: 'Profile' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id 
                    ? 'border-[#E31837] text-[#012169]' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-[#012169] to-[#01337d] rounded-2xl p-8 text-white mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold mb-2">
                Welcome back, {consumer?.first_name || 'Valued Customer'}!
              </h1>
              <p className="text-gray-200">
                {applications.length > 0 
                  ? `You have ${applications.length} active application(s)` 
                  : 'Start your credit enhancement journey today'}
              </p>
            </div>
            <Link
              to="/beth-finance/apply"
              className="bg-[#E31837] hover:bg-[#c4142f] px-6 py-3 rounded-lg font-semibold flex items-center transition-all"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Application
            </Link>
          </div>
        </div>

        {activeTab === 'overview' && (
          <>
            {/* Quick Actions */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Link 
                to="/beth-finance/apply?type=auto"
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-[#012169]/10 rounded-xl flex items-center justify-center">
                    <Car className="w-6 h-6 text-[#012169]" />
                  </div>
                  <span className="text-[#E31837] font-bold">$49</span>
                </div>
                <h3 className="font-bold text-[#012169] mb-1">Auto Loan</h3>
                <p className="text-sm text-gray-600 mb-3">New or used vehicle financing</p>
                <span className="text-[#E31837] text-sm font-medium flex items-center">
                  Apply Now <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>

              <Link 
                to="/beth-finance/apply?type=mortgage"
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-[#012169]/10 rounded-xl flex items-center justify-center">
                    <Home className="w-6 h-6 text-[#012169]" />
                  </div>
                  <span className="text-[#E31837] font-bold">$199</span>
                </div>
                <h3 className="font-bold text-[#012169] mb-1">Mortgage</h3>
                <p className="text-sm text-gray-600 mb-3">Purchase or refinance</p>
                <span className="text-[#E31837] text-sm font-medium flex items-center">
                  Apply Now <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>

              <Link 
                to="/beth-finance/pas/order"
                className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-purple-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-200 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                  <span className="text-purple-600 font-bold text-xs">FROM $199</span>
                </div>
                <h3 className="font-bold text-purple-900 mb-1">PAS Report</h3>
                <p className="text-sm text-purple-700 mb-3">Credit Enhancement Analysis</p>
                <span className="text-purple-600 text-sm font-medium flex items-center">
                  Order Report <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <h3 className="font-bold text-[#012169] mb-1">Credit Enhancement</h3>
                <p className="text-sm text-gray-600 mb-3">Improve your approval odds</p>
                <span className="text-green-600 text-sm font-medium flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" /> Active
                </span>
              </div>
            </div>

            {/* Recent Applications */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold text-[#012169]">Recent Applications</h2>
                  <button 
                    onClick={() => setActiveTab('applications')}
                    className="text-[#E31837] text-sm font-medium hover:underline"
                  >
                    View All
                  </button>
                </div>
              </div>
              
              {applications.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {applications.slice(0, 3).map((app, idx) => (
                    <div key={idx} className="p-6 flex items-center justify-between hover:bg-gray-50 transition-colors">
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          app.loan_type === 'mortgage' ? 'bg-purple-100' : 'bg-blue-100'
                        }`}>
                          {app.loan_type === 'mortgage' ? (
                            <Home className={`w-5 h-5 ${app.loan_type === 'mortgage' ? 'text-purple-600' : 'text-blue-600'}`} />
                          ) : (
                            <Car className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {app.loan_type === 'mortgage' ? 'Mortgage' : 'Auto'} Loan Application
                          </p>
                          <p className="text-sm text-gray-500">
                            Applied {new Date(app.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                          {getStatusIcon(app.status)}
                          <span className="ml-1 capitalize">{app.status}</span>
                        </span>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications Yet</h3>
                  <p className="text-gray-500 mb-6">Start your credit enhancement journey today</p>
                  <Link
                    to="/beth-finance/apply"
                    className="inline-flex items-center px-6 py-3 bg-[#E31837] hover:bg-[#c4142f] text-white rounded-lg font-semibold transition-all"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Start Application
                  </Link>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'applications' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-[#012169]">All Applications</h2>
            </div>
            
            {applications.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Application ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {applications.map((app, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {app.application_id || `APP-${idx + 1}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">
                          {app.loan_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${app.loan_amount?.toLocaleString() || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(app.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                            {getStatusIcon(app.status)}
                            <span className="ml-1 capitalize">{formatStatus(app.status)}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {['submitted_to_lenders', 'offer_accepted', 'payment_complete'].includes(app.status) || app.payment_status === 'test_mode' || app.payment_status === 'completed' ? (
                            <Link 
                              to={`/beth-finance/lenders/${app.application_id}`}
                              className="text-[#E31837] hover:underline font-medium"
                            >
                              View Lenders
                            </Link>
                          ) : app.status === 'pending_payment' ? (
                            <span className="text-gray-400">Payment Required</span>
                          ) : (
                            <button className="text-[#012169] hover:underline font-medium">View Details</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications</h3>
                <p className="text-gray-500">You have not submitted any applications yet.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'offers' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-[#012169]">Loan Offers</h2>
              <p className="text-sm text-gray-500">Compare and accept offers from our lender network</p>
            </div>
            
            {allOffers.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {allOffers.map((offer, idx) => (
                  <div key={idx} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          offer.loan_type === 'mortgage' ? 'bg-purple-100' : 'bg-blue-100'
                        }`}>
                          {offer.loan_type === 'mortgage' ? (
                            <Home className="w-6 h-6 text-purple-600" />
                          ) : (
                            <Car className="w-6 h-6 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Building2 className="w-4 h-4 text-[#012169]" />
                            <span className="font-bold text-gray-900">{offer.lender_name}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              offer.status === 'accepted' ? 'bg-green-100 text-green-700' :
                              offer.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {offer.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mb-3">
                            {offer.loan_type?.charAt(0).toUpperCase() + offer.loan_type?.slice(1)} Loan • {offer.application_id}
                          </p>
                          
                          <div className="flex gap-6">
                            <div className="flex items-center gap-1.5">
                              <Percent className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600">APR:</span>
                              <span className="font-bold text-[#012169]">{offer.interest_rate}%</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <DollarSign className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600">Monthly:</span>
                              <span className="font-bold text-green-600">${offer.monthly_payment?.toLocaleString()}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600">Term:</span>
                              <span className="font-bold text-gray-900">{offer.loan_term_months} mo</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {offer.status === 'accepted' ? (
                          <div className="flex items-center gap-2 px-4 py-2 bg-green-100 rounded-lg text-green-700">
                            <CheckCircle className="w-4 h-4" />
                            <span className="font-medium">Accepted</span>
                          </div>
                        ) : offer.status === 'pending' ? (
                          <Link
                            to={`/beth-finance/lenders/${offer.application_id}`}
                            className="flex items-center gap-2 px-4 py-2 bg-[#E31837] hover:bg-[#c4142f] rounded-lg text-white font-medium transition-colors"
                          >
                            View Offer
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                        ) : (
                          <span className="text-gray-400 text-sm">Declined</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Loan Offers Yet</h3>
                <p className="text-gray-500 mb-6">Complete a loan application and submit to lenders to receive offers.</p>
                <Link
                  to="/beth-finance/apply"
                  className="inline-flex items-center px-6 py-3 bg-[#012169] hover:bg-[#01337d] text-white rounded-lg font-semibold transition-all"
                >
                  Start Application
                </Link>
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-[#012169] mb-6">Profile Information</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
                <p className="text-gray-900">{consumer?.first_name} {consumer?.last_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                <p className="text-gray-900">{consumer?.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Phone</label>
                <p className="text-gray-900">{consumer?.phone || '—'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Address</label>
                <p className="text-gray-900">
                  {consumer?.address ? `${consumer.address}, ${consumer.city}, ${consumer.state} ${consumer.zip_code}` : '—'}
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ConsumerDashboard;
